import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import ChatbotLogin from "./components/ChatbotLogin";
import ChatbotRegister from "./components/ChatbotRegister";
import ChatbotConfig from "./components/ChatbotConfig";
import MDSnackbar from "components/MDSnackbar";
import Widget from "../../widget/Widget";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Link } from "react-router-dom";
import appConfig from "../../config";

function Chatbot() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [chatbotInfo, setChatbotInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewConfig, setPreviewConfig] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, color: "success", message: "" });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [integrationTab, setIntegrationTab] = useState(0);

  const handleIntegrationTabChange = (event, newValue) => {
    setIntegrationTab(newValue);
  };

  const htmlCode = `<script src="https://rieshim.app/widget.js" chatbot-id="${token}"></script>`;

  const reactCode = `import React, { useEffect } from 'react';

function ChatbotScript() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://rieshim.app/widget.js';
    script.setAttribute('chatbot-id', '${token}');
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const scriptTag = document.querySelector('script[src="https://rieshim.app/widget.js"]');
      if (scriptTag) document.body.removeChild(scriptTag);
      
      // The widget might create its own elements, you may need a more specific
      // cleanup function depending on how the widget is structured.
      // For example, if it creates a div with a specific ID:
      // const widgetEl = document.getElementById('rieshim-chatbot-widget');
      // if (widgetEl) widgetEl.remove();
    }
  }, []);

  return null;
}`;

  const nextjsCode = `import Script from 'next/script';

function Chatbot() {
  return (
    <Script 
      src="https://rieshim.app/widget.js" 
      chatbot-id="${token}" 
      strategy="lazyOnload" 
    />
  )
}

export default Chatbot;
`;

  const codeSnippets = [
    { lang: "html", code: htmlCode },
    { lang: "jsx", code: reactCode },
    { lang: "jsx", code: nextjsCode },
  ];

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(codeSnippets[integrationTab].code);
      showSnackbar("success", "Kód bol skopírovaný do schránky.");
    } catch {
      showSnackbar("error", "Nepodarilo sa skopírovať kód.");
    }
  };

  const stableConfigForEditor = useMemo(() => {
    const chatbotConfig = chatbotInfo?.config || {};
    let parsed =
      typeof chatbotConfig === "string"
        ? (() => {
            try {
              return JSON.parse(chatbotConfig);
            } catch {
              return {};
            }
          })()
        : chatbotConfig;
    return {
      ...parsed,
      name: chatbotInfo?.name || parsed.name,
      avatar_url: chatbotInfo?.avatar_url,
    };
  }, [chatbotInfo]);

  useLayoutEffect(() => {
    let changed = false;
    if (location.state && location.state.token) {
      setToken(location.state.token);
      changed = true;
    }
    if (location.state && location.state.showRegister) {
      setShowRegister(true);
      changed = true;
    }
    // Vyčisti state po použití, aby sa neaplikoval znova pri ďalšej navigácii
    if (changed) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const fetchChatbotInfo = async () => {
      if (!token) {
        setChatbotInfo(null);
        return;
      }
      try {
        const meRes = await fetch(`${appConfig.BASE_URL}/v1/chatbot/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!meRes.ok) throw new Error("Neplatný token alebo chyba servera");
        const meData = await meRes.json();

        // Po overení tokenu načítaj kompletné dáta z nášho node.js backendu
        const fullDataRes = await fetch(`${appConfig.BASE_URL}/api/chatbots/${meData.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!fullDataRes.ok) throw new Error("Chyba pri načítaní detailov chatbota");

        const fullData = await fullDataRes.json();
        setChatbotInfo(fullData);
      } catch (err) {
        setChatbotInfo(null);
        setToken("");
        alert("Neplatný token alebo chyba servera");
      }
    };
    fetchChatbotInfo();
  }, [token]);

  const showSnackbar = (color, message) => {
    setSnackbar({ open: true, color, message, dateTime: new Date().toLocaleTimeString() });
  };
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleSaveConfig = async (newConfig) => {
    setSaving(true);
    try {
      const id = chatbotInfo.id;
      const configToSave = { ...newConfig };
      delete configToSave.name;
      delete configToSave.avatar_url; // Avatar sa ukladá samostatne
      const res = await fetch(`${appConfig.BASE_URL}/api/chatbots/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ config: configToSave, name: newConfig.name }),
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          showSnackbar("error", "Chyba: Nie ste prihlásený alebo nemáte oprávnenie.");
        } else {
          const errData = await res.json();
          throw new Error(errData.message || "Chyba pri ukladaní konfigurácie");
        }
        return;
      }

      // Po úspešnom uložení načítaj znova kompletné dáta (GET je verejný)
      const fullDataRes = await fetch(`${appConfig.BASE_URL}/api/chatbots/${id}`);
      if (fullDataRes.ok) {
        const fullData = await fullDataRes.json();
        setChatbotInfo(fullData);
      } else {
        setChatbotInfo((prev) => ({ ...prev, name: newConfig.name, config: newConfig }));
      }

      setPreviewConfig(null);
      showSnackbar("success", "Konfigurácia bola úspešne uložená.");
    } catch (err) {
      showSnackbar("error", err.message || "Chyba pri ukladaní konfigurácie");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChatbot = async () => {
    try {
      const id = chatbotInfo.id;
      const res = await fetch(`${appConfig.BASE_URL}/api/chatbots/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {},
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          showSnackbar("error", "Chyba: Nie ste prihlásený alebo nemáte oprávnenie.");
        } else {
          const errData = await res.json();
          throw new Error(errData.message || "Chyba pri mazaní chatbota");
        }
        return;
      }
      showSnackbar("success", "Chatbot bol úspešne vymazaný.");
      setToken(""); // odhlásim používateľa
    } catch (err) {
      showSnackbar("error", err.message || "Chyba pri mazaní chatbota");
    }
  };

  return (
    <DashboardLayout>
      {token && chatbotInfo ? (
        <>
          <MDBox mb={3} px={{ xs: 1, sm: 2, md: 3 }}>
            <MDTypography variant="h4" fontWeight="medium" mb={0.5}>
              Chatbot Configuration
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Configure your AI chatbot settings and behavior
            </MDTypography>
          </MDBox>
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Stack spacing={3}>
                  <ChatbotConfig
                    config={stableConfigForEditor}
                    onSave={handleSaveConfig}
                    onConfigChange={setPreviewConfig}
                    loading={saving}
                    token={token}
                    chatbotID={chatbotInfo.id}
                    onDelete={handleDeleteChatbot}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Box
                  sx={{
                    width: { xs: "100%", md: 400 },
                    maxWidth: "100%",
                    mx: "auto",
                    position: { lg: "sticky" },
                    top: "24px",
                  }}
                >
                  <Stack spacing={2} alignItems="stretch">
                    <Widget
                      config={previewConfig || stableConfigForEditor}
                      token={token}
                      adminMode={true}
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      type="button"
                      onClick={() =>
                        document.querySelector("form") &&
                        document.querySelector("form").requestSubmit()
                      }
                      disabled={saving}
                      fullWidth
                    >
                      {saving ? "Saving..." : "Save and publish"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => setIntegrationDialogOpen(true)}
                      fullWidth
                    >
                      Embed on website
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="error"
                      onClick={() => setDeleteDialog(true)}
                      fullWidth
                    >
                      Delete chatbot
                    </MDButton>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </MDBox>
          <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
            <DialogTitle>Confirm deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this chatbot? This action is irreversible.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => setDeleteDialog(false)} color="info">
                Cancel
              </MDButton>
              <MDButton
                onClick={() => {
                  setDeleteDialog(false);
                  handleDeleteChatbot();
                }}
                color="error"
              >
                Delete
              </MDButton>
            </DialogActions>
          </Dialog>
          <Dialog
            open={integrationDialogOpen}
            onClose={() => setIntegrationDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Embed Chatbot on Your Website</DialogTitle>
            <DialogContent>
              <MDBox>
                <Tabs
                  value={integrationTab}
                  onChange={handleIntegrationTabChange}
                  aria-label="integration code tabs"
                >
                  <Tab label="HTML" />
                  <Tab label="React" />
                  <Tab label="Next.js" />
                </Tabs>
                <MDBox pt={2} sx={{ "& .MuiTabs-indicator": { zIndex: 1 } }}>
                  <SyntaxHighlighter
                    language={codeSnippets[integrationTab].lang}
                    style={oneDark}
                    customStyle={{
                      borderRadius: 8,
                      fontSize: 14,
                      margin: 0,
                      padding: "18px 24px",
                      background: "#23272e",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                    showLineNumbers={false}
                    wrapLongLines={true}
                  >
                    {codeSnippets[integrationTab].code.trim()}
                  </SyntaxHighlighter>
                </MDBox>
                <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="body2">
                    Need more help? Check out our{" "}
                    <MDTypography
                      component={Link}
                      to="/docs"
                      variant="body2"
                      color="info"
                      fontWeight="medium"
                    >
                      documentation
                    </MDTypography>
                    .
                  </MDTypography>
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<Icon>content_copy</Icon>}
                    onClick={handleCopyCode}
                  >
                    Copy Code
                  </MDButton>
                </MDBox>
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => setIntegrationDialogOpen(false)} color="info">
                Close
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      ) : showRegister ? (
        <ChatbotRegister setToken={setToken} onShowLogin={() => setShowRegister(false)} />
      ) : (
        <ChatbotLogin setToken={setToken} onShowRegister={() => setShowRegister(true)} />
      )}
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Úspech" : "Chyba"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
        dateTime={snackbar.dateTime || new Date().toLocaleTimeString()}
      />
    </DashboardLayout>
  );
}

export default Chatbot;
